import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatAccountLabel, type Account } from '@/features/accounts/accounts.api';
import {
  accountQrQueryKey,
  qrErrorStatus,
  QR_REFRESH_INTERVAL_MS,
  useAccountQr,
} from '@/features/accounts/useAccountQr';

interface QrRequiredDialogProps {
  account: Account;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * QR pairing dialog for an account in `qr_required` state (M6-T3).
 *
 * Wires the Hub's `GET /accounts/{id}/qr` endpoint to a self-refreshing image
 * the customer scans with WhatsApp. The dialog:
 *
 *   - Polls every 15s (`useAccountQr`) so the gateway's rotating QR stays fresh.
 *   - Watches the `account.status` prop (driven by `useAccounts` in the parent
 *     `AccountRow`, which re-renders this dialog when the cache row updates).
 *     When the status flips off `qr_required` (typically to `connecting`, then
 *     `connected`) we auto-close and toast a success. The WS `account.status`
 *     -> cache invalidation fan-out (projection.ts) drives this transition.
 *   - Closes silently on 404 (account vanished) or 409 (status no longer
 *     `qr_required` — the backend echoes the live status in its 409 body).
 *   - Resets the cache on close so the next open starts with a fresh fetch
 *     rather than a stale poll.
 *
 * The dialog never blocks the rest of the console: react-query polling
 * happens in the background and `enabled=open` means the polling stops the
 * moment the dialog closes. The dialog deliberately does NOT call
 * `useAccounts()` itself — that would register an extra cache observer per
 * AccountRow and combine badly with `useAccountUnreadSum`'s cache subscription
 * to lock the AccountSidebar test's `user.click` event loop.
 */
export function QrRequiredDialog({
  account,
  open,
  onOpenChange,
}: QrRequiredDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const { data: qr, error, isLoading, isFetching, refetch } = useAccountQr(
    account.id,
    { enabled: open },
  );
  // Phone-derived display name (never the raw external id — see
  // `formatAccountLabel`). The `account` prop is the live cache row threaded
  // down by AccountRow, so a pairing-time rename re-renders this too.
  const label = formatAccountLabel(account);

  // Track whether we've already toasted a successful pairing so a rapid
  // status flip back to qr_required (re-pair) doesn't re-fire the toast.
  const successToastFiredRef = useRef(false);

  // Auto-close on a successful pairing: the row leaves `qr_required` (the
  // first sign of success is `connecting`, then `connected`). The status
  // value comes from the parent AccountRow, which is driven by
  // `useAccounts` — so a WS-driven `setQueryData` update (M6-T3 projection)
  // re-renders us with the new status and the effect fires.
  useEffect(() => {
    if (!open) return;
    if (account.status !== 'qr_required' && !successToastFiredRef.current) {
      successToastFiredRef.current = true;
      if (account.status === 'connected') {
        toast.success(`Connected — ${label} is paired.`);
      } else if (account.status === 'connecting') {
        // Scan succeeded; gateway is finalizing the handshake. Surface a
        // softer message and close — the next status event will be
        // `connected` and the sidebar dot will turn green on its own.
        toast.success(`${label}: scan accepted, connecting...`);
      }
      onOpenChange(false);
    }
  }, [account.status, onOpenChange, open, label]);

  // Close on 404 / 409: account vanished or is no longer awaiting QR. A 409
  // typically means another tab / ops re-paired the account first; close
  // quietly and trust the WS-driven cache to land the new status. A 404 is
  // the account row being deleted — should be rare; toast clearly.
  useEffect(() => {
    if (!open) return;
    const status = qrErrorStatus(error);
    if (status === 404) {
      toast.error(`${label} is no longer available.`);
      onOpenChange(false);
    } else if (status === 409) {
      // Defensive — the live-status useEffect above should have closed us
      // already, but a network race can land the 409 first. Close quietly.
      onOpenChange(false);
    }
  }, [error, onOpenChange, open, label]);

  // On a TRANSITION from open -> closed (not on initial mount with open=false),
  // drop the per-account QR cache so a re-open starts with a fresh fetch
  // instead of replaying a stale image. Tracking the previous value with a
  // ref avoids firing at the very first render — the dialog mounts with
  // open=false alongside the AccountRow it lives under, and a `removeQueries`
  // there is a wasted cache call. Also clears the success-toast latch so a
  // future re-pair surfaces its own toast.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      successToastFiredRef.current = false;
      queryClient.removeQueries({ queryKey: accountQrQueryKey(account.id) });
    }
    prevOpenRef.current = open;
  }, [open, queryClient, account.id]);

  const errorStatus = qrErrorStatus(error);
  // The 409 / 404 / connected branches all auto-close via useEffects above —
  // those cases never reach the visible-error path. Only show an inline error
  // for 503 (gateway down) or an unexpected non-HTTP failure (network blip).
  const showInlineError =
    error !== null && errorStatus !== 404 && errorStatus !== 409;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Re-pair WhatsApp account</DialogTitle>
          <DialogDescription>
            Scan this code with WhatsApp on the customer phone for{' '}
            <span className="font-medium text-foreground">{label}</span>{' '}
            (Settings &rarr; Linked devices &rarr; Link a device).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3 py-4">
          {isLoading && (
            <div className="flex h-64 w-64 items-center justify-center rounded-md border border-dashed bg-muted/30">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && showInlineError && (
            <div className="flex h-64 w-64 flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 px-4 text-center text-sm text-muted-foreground">
              <p>The gateway is unreachable right now.</p>
              <p className="text-xs">Retrying every 15 seconds.</p>
            </div>
          )}

          {!isLoading && !showInlineError && qr && qr.image_base64 && (
            <img
              src={`data:image/png;base64,${qr.image_base64}`}
              alt={`WhatsApp pairing QR for ${label}`}
              className="size-64 rounded-md border bg-white p-2"
            />
          )}

          {!isLoading && !showInlineError && qr && !qr.image_base64 && (
            <div className="flex h-64 w-64 flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 px-4 text-center text-sm text-muted-foreground">
              <Loader2 className="size-6 animate-spin" />
              <p>Waiting for the gateway to generate a fresh QR...</p>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Refreshing every {QR_REFRESH_INTERVAL_MS / 1000}s.{' '}
            {isFetching && qr ? 'Updating...' : null}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={isFetching ? 'mr-2 size-4 animate-spin' : 'mr-2 size-4'}
            />
            Refresh
          </Button>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
